import 'selectize';
import { FormValidator } from './FormValidator';
import { AsyncStorage } from '../../util/asyncStorage';
import * as params from '../../util/getQueryParams';


/**
 * the packages below are available for all locations, if the package is not available
 * Skoleo will send the general inschool info package
 */
export const GENERAL_INFO_PACKAGES = [
  { valueField: 'basisschoolbegeleiding', labelField: 'Basisschoolbegeleiding' },
  { valueField: 'codelab', labelField: 'Codelab' },
  { valueField: 'bijles', labelField: 'Bijles' },
  { valueField: 'examenjaar', labelField: 'Het Lyceo Examenjaar/ Eindexamensprint' },
  { valueField: 'bijles_online', labelField: 'Online Bijles' },
  { valueField: 'examentraining', labelField: 'Examentraining' },
  { valueField: 'huiswerkbegeleiding', labelField: 'Huiswerkbegeleiding' },
];

/**
 * The packages bellow are only available for specific locations, so we first
 * check if they are available for the selected location before providing them
 * as an options for the user
 */
export const ANXIETY_PACKAGE = { valueField: 'faalangsttraining', labelField: 'Faalangsttraining' };
export const HOMEWORK_PACKAGE = { valueField: 'huiswerkklas', labelField: 'Huiswerkklas' };
export const HIGH_SCHOOL_PACKAGE = { valueField: 'brugklastraining', labelField: 'Brugklastraining' };

/**
 * @documentation
 * /docs/formulier-informatie-pakket.md
 */
export class FormInfoPakket {
  constructor() {
    this.$form = $('#js-information-form');
    this.storage = new AsyncStorage();

    // Clear historical prefilled data when no errors.
    if (!this.$form.is('[data-error]')) {
      this.storage.removeItem('packages');
      this.storage.removeItem('location');
    }

    if (this.$form.length === 0) {
      return;
    }

    // the array of ProductInformatieAanvraag from the database set in twig
    this.products = products;

    this.$productFields = $('#js-product-fields');

    this.$packagesSelectize = this.initializePackagesDropdown();
    this.$locationSelectize = this.initializeLocationDropdown();

    this.preFillPackagesField();

    this.$locationSelectize.on('change', /* istanbul ignore next */ id => this.populatePackagesDropdown(id));

    if (this.$packagesSelectize) {
      this.$packagesSelectize.on(
        'change',
        /* istanbul ignore next */ packages => this.toggleLocationDropdown(packages)
      );
    }

    this.validator = new FormValidator(this.$form);
  }

  /**
     * Only display the location dropdown if the product hwb has been selected.
     * Do nothing if the location has been provided as query parameter.
     */
  async toggleLocationDropdown(packages) {
    await this.storage.setItem('packages', packages);
    const { locatie } = params.getQueryParams(document.location.search);

    if (locatie || !packages) {
      return;
    }

    if (packages.includes('huiswerkbegeleiding')) {
      this.$productFields.show();
    } else {
      this.$productFields.hide();
    }
  }

  /**
     * Get values from the query parameters and populate the
     * related fields.
     */
  async preFillPackagesField() {
    const { locatie, dienst } = params.getQueryParams(document.location.search);

    if (locatie) {
      await this.storage.setItem('location', locatie);
    }

    const location = await this.storage.getItem('location');

    if (location) {
      this.populatePackagesDropdown(parseInt(location, 10));
      this.$locationSelectize.setValue(location);
      this.$productFields.show();
    }

    if (dienst) {
      await this.storage.setItem('packages', dienst);
    }

    const packages = await this.storage.getItem('packages');
    if (packages) {
      packages.split(',').forEach(packet => this.$packagesSelectize.addItem(packet));
    }

  }

  /**
     * Check whether the selected location is a check5 website.
     */
  async populatePackagesDropdown(locationId) {
    await this.storage.setItem('location', locationId);
    const options = this.getLocationPackages(locationId);
    this.$packagesSelectize.clearOptions();
    this.$packagesSelectize.addOption(options);
  }

  /**
     * Check the available information types for the provided location and
     * return the options for the package dropdown.
     */
  getLocationPackages(locationId) {
    const location = this.getLocation(locationId);

    if (!location) {
      return GENERAL_INFO_PACKAGES;
    }

    const informationTypes = location.product_informatie_type.split(',');

    const extraPackages = [ANXIETY_PACKAGE, HOMEWORK_PACKAGE, HIGH_SCHOOL_PACKAGE]
      .filter(infoPackage => informationTypes.includes(infoPackage.valueField));

    return [
      ...GENERAL_INFO_PACKAGES,
      ...extraPackages,
    ];
  }

  /**
     * Initialize the products dropdown.
     */
  initializePackagesDropdown(options = GENERAL_INFO_PACKAGES) {
    return $('#js-info-product').selectize({
      maxItems: null,
      options: options,
      valueField: 'valueField',
      labelField: 'labelField',
      render: {
        item: /* istanbul ignore next */ item => this.renderPackageItem(item),
        option: /* istanbul ignore next */ item => this.renderPackageDropdownItem(item),
      },
      create: false,
    })[0].selectize;

  }

  /**
     * Init the jquery select/search box for the tutor locations.
     *
     * Watch the location field for changes and update the package
     * field accordingly.
     */
  initializeLocationDropdown() {
    const locationField = $('#js-info-location')
      .selectize({
        options: this.products,
        searchField: ['loc_stad', 'loc_naam'],
        valueField: 'valueField',
        labelField: 'labelField',
        render: {
          option: /* istanbul ignore next */ item => this.renderLocationItem(item),
        },
        create: false,
      })[0].selectize;

    locationField.setValue('');

    return locationField;
  }

  /**
     * Render the html for the selected items.
     */
  renderLocationItem(item) {
    const location = this.getLocation(item.valueField);

    return (
      '<div class="selectize-option">'
                + `<strong>${item.labelField}</strong>`
                + `<img src="${location.label_logo_url}" alt="${location.label_naam} logo">`
            + '</div>'
    );
  }

  /**
     * Render the html for the selected items.
     */
  renderPackageItem(item) {
    return (
      '<div class="item">'
                + '<i class="fa-regular fa-file-lines"></i>'
                + item.labelField
            + '</div>'
    );
  }

  /**
     * Return the html for a single info item.
     */
  renderPackageDropdownItem(item) {
    return (
      '<div class="selectize-info-package">'
                + '<div class="selectize-info-package__front">'
                    + '<div class="selectize-info-package__balloon">'
                        + '<strong>Informatie</strong>'
                        + item.valueField.slice(0, 15)
                    + '</div>'
                + '<div class="selectize-info-package__sub-balloon"></div>'
            + '</div>'
            + '<div class="selectize-info-package__content">'
                + '<div class="selectize-info-package__name">' + item.labelField + '</div>'
                + '<div class="selectize-info-package__sub-title">Brochure</div>'
            + '</div>'
                + '<div class="selectize-info-package__price">Gratis</div>'
            + '</div>'
    );
  }

  /**
     * Search the array of products (locations) for the
     * specified locationId.
     */
  getLocation(locationId) {
    return this.products.find(
      product => +product.id_product === +locationId
    );
  }
}
